import { formatDate } from "../../../utils/datetime";

/**
 * Format a work scope's created timestamp for the table.
 *
 * Below the `sm` breakpoint the cell shows `MM/DD/YY`. From there up it shows
 * the long form.
 *
 * @param {string|null|undefined} value - ISO datetime from the API.
 * @returns {JSX.Element|string}
 */
function formatCreated(value) {
  if (!value) return "";
  const iso = String(value).slice(0, 10);
  const longDate = formatDate(iso, "month DD, YYYY");
  const shortDate = formatDate(iso, "MM/DD/YY");
  return (
    <>
      <span className="sm:hidden">{shortDate}</span>
      <span className="hidden sm:inline">{longDate}</span>
    </>
  );
}

/**
 * Select value for a row's unit, whether it is nested or a primary key.
 *
 * @param {Object} row - Work scope row.
 * @returns {string}
 */
function unitValue(row) {
  if (row.unit == null || row.unit === "") return "";
  if (typeof row.unit === "object") return String(row.unit.id ?? "");
  return String(row.unit);
}

/**
 * Column config for the work scope management table.
 *
 * @param {Array<{id: number, name: string}>} units - Unit of measure options.
 * @returns {Array<Object>}
 */
export function buildWorkScopeColumns(units) {
  const unitOptions = [
    { value: "", label: "—" },
    ...(Array.isArray(units) ? units : []).map((unit) => ({
      value: String(unit.id),
      label: unit.name,
    })),
  ];

  return [
    {
      key: "title",
      label: "Title",
      type: "text",
      // Floor before the table scrolls. Longer names ellipsize when truncate itrue.
      minWidth: "9rem",
      truncate: true,
      autoFocus: true,
      placeholder: "Title",
    },
    {
      key: "unit",
      label: "Unit",
      type: "select",
      minWidth: "6.5rem",
      maxWidth: "12rem",
      truncate: true,
      options: unitOptions,
      getValue: unitValue,
      getSortValue: (row) => row.unit?.name || "",
      getSearchValue: (row) => row.unit?.name || "",
      toPatch: (value) => ({
        unit: value === "" || value == null ? null : Number(value),
      }),
    },
    {
      key: "created",
      label: "Created",
      type: "readonly",
      minWidth: "4.75rem",
      align: "right",
      truncate: false,
      getDisplay: (row) => formatCreated(row.created),
      getSortValue: (row) => row.created || "",
      getSearchValue: (row) => row.created || "",
    },
    {
      key: "count",
      label: "Count",
      type: "readonly",
      minWidth: "4.75rem",
      truncate: false,
      align: "right",
      headerTitle:
        "Count of dispatch dates that reference this scope, excluding skipped days.",
      getDisplay: (row) => Number(row.planning_usage_count) || 0,
      getSortValue: (row) => Number(row.planning_usage_count) || 0,
    },
    {
      key: "active",
      label: "Active",
      type: "checkbox",
      minWidth: "4.75rem",
      truncate: false,
      align: "center",
      sortable: false,
    },
  ];
}
